import axios from 'axios'
import type { AxiosInstance } from 'axios'
import { ElNotification } from 'element-plus'
import { useAuthStore } from '~/stores/auth'

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  // 인터셉터 콜백은 Nuxt 컨텍스트 밖에서 실행되므로 라우터를 셋업 시점에 캡처한다.
  const router = useRouter()
  const baseURL = import.meta.server
    ? (config.apiBase as string || config.public.apiBase)
    : config.public.apiBase
  const api: AxiosInstance = axios.create({
    baseURL,
    timeout: 10000,
    headers: { 'Content-Type': 'application/json' },
  })

  api.interceptors.request.use(async (config) => {

    const authStore = useAuthStore()

    // F5 부트스트랩 가드: accessToken 은 없지만 refreshToken 은 있는 상태(새로고침 직후).
    // 컴포넌트들이 mount 되며 토큰 발급(app:mounted)보다 먼저 보호 API 를 호출하면
    // 각 요청이 401 → refresh → 재요청으로 이어진다(/me/menus, /dashboard/stats,
    // /notifications/me/unread-count, /api/i18n/languages/active 등).
    // 여기서 요청을 보내기 전에 토큰을 먼저 확보하면 401 왕복이 모두 사라진다.
    // refresh() 는 single-flight 라 동시 다발 요청도 refresh 를 한 번만 트리거한다.
    const isAuthEndpoint = ['/auth/login', '/auth/refresh', '/auth/logout'].some(
      (p) => config.url?.includes(p)
    )
    if (process.client && !authStore.accessToken && authStore.refreshToken && !isAuthEndpoint) {
      try {
        await authStore.refresh()
      } catch {
        // refresh 실패 시 그대로 진행 → 응답 interceptor 의 401 처리(세션 만료)로 넘어간다.
      }
    }

    if (authStore.accessToken) {
      config.headers.Authorization = `Bearer ${authStore.accessToken}`
    }

    // 호출 출처 메뉴 추적: 현재 라우트를 X-Menu-Path 로 전달 → 백엔드 ACC_API 로그에 '어느 메뉴에서 호출했는지' 기록
    if (process.client) {
      const path = router.currentRoute.value?.path
      if (path && (path.startsWith('/base') || path.startsWith('/biz'))) {
        config.headers['X-Menu-Path'] = path
      }
    }
    // FormData 전송 시 Content-Type을 제거한다.
    // 브라우저/Node.js가 multipart/form-data; boundary=... 를 자동으로 완성해야 하는데,
    // 인스턴스 default 'application/json'이 남아 있으면 boundary 없이 덮어써져
    // 서버에서 HttpMediaTypeNotSupportedException이 발생한다.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
      config.timeout = 0  // 파일 업로드는 타임아웃 없음
    }
    return config
  })

  let isRefreshing = false
  let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = []

  const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })
    failedQueue = []
  }

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      const isAuthEndpoint = ['/auth/login', '/auth/refresh', '/auth/logout'].some(
        (p) => originalRequest.url?.includes(p)
      )

      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        const authStore = useAuthStore()
        try {
          await authStore.refresh()
          processQueue(null, authStore.accessToken)
          originalRequest.headers.Authorization = `Bearer ${authStore.accessToken}`
          return api(originalRequest)
        } catch (refreshError) {
          // Refresh token도 만료 — 화면 안의 인-페이지 모달로 세션 복구 시도.
          // 별도 브라우저 창(window.open)을 쓰지 않으므로 팝업 차단 이슈가 없다.
          authStore.$patch({ accessToken: null, refreshToken: null, user: null })
          if (process.client) {
            document.cookie = 'refreshToken=; max-age=0; path=/; samesite=lax'
          }

          if (process.client) {
            try {
              // SessionExpiredModal.vue 가 sessionRecovering 을 보고 모달을 띄운다.
              // 사용자가 모달에서 재로그인하면 새 accessToken 으로 resolve 된다.
              // login() 이 refreshToken 쿠키/스토어까지 갱신하므로 여기서는 토큰만 받으면 된다.
              const newToken = await authStore.requestSessionRecovery()

              processQueue(null, newToken)
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              return api(originalRequest)
            } catch {
              // 모달에서 로그아웃/취소 → /login 으로 이동
              processQueue(refreshError, null)
              await authStore.logout()
              navigateTo('/login')
              return Promise.reject(refreshError)
            }
          }

          authStore.$patch({ accessToken: null, refreshToken: null, user: null })
          processQueue(refreshError, null)
          navigateTo('/login')
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      // 백그라운드 텔레메트리(메뉴 접속 기록 등)는 실패해도 사용자에게 알림을 띄우지 않는다.
      const isSilentEndpoint = ['/access-logs/menu-access'].some(
        (p) => originalRequest.url?.includes(p)
      )

      if (typeof window !== 'undefined' && !isAuthEndpoint && !isSilentEndpoint) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          '알 수 없는 오류가 발생했습니다.'
        ElNotification({
          title: '오류',
          message,
          type: 'error',
          position: 'bottom-left',
          duration: 4000,
        })
      }

      return Promise.reject(error)
    }
  )

  return {
    provide: {
      api,
    },
  }
})
